import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { EMPTY_LIST } from '../../../constants/common'
import {
  useIsProviderProfileNGAvailable,
  useIsTeamVisibilityHidden,
  useIsUserProfileNGAvailable,
  useUserData,
} from '../../../hooks'
import {
  getUserProfileRoute,
  isTeamUserProfileHidden,
} from '../../../utils/user'
import { ChatroomMessageBody } from '../ChatroomMessageBody'
import { ChatroomMessageHeader } from '../ChatroomMessageHeader'

import {
  formatChatroomMessageTime,
  getChatroomMessageDisplayHeaderText,
  getChatroomMessageLikeCount,
  isChatroomMessageLiked,
  isOwnChatroomMessage,
} from './ChatroomMessage.utils'
import { useChatroomMessageLike } from './useChatroomMessageLike'

import * as styles from './ChatroomMessage.module.css'

function ChatroomMessage({
  currentUserId,
  filePreviewProps,
  isPrivate,
  message,
  outboundUuid,
  onEditMessage,
  onRefetchVisibleMessages,
  onUpdateMessage,
}) {
  const { t } = useTranslation()

  const files = message.files || EMPTY_LIST

  const fullName = message.owner_obj?.fullname
  const isOwnMessage = isOwnChatroomMessage({ currentUserId, message })

  const isLiked = isChatroomMessageLiked(message)
  const likeCount = getChatroomMessageLikeCount(message)

  const timeLabel = formatChatroomMessageTime(message.created)

  const isProviderProfileNGAvailable = useIsProviderProfileNGAvailable()
  const isUserProfileNGAvailable = useIsUserProfileNGAvailable()
  const role = message.owner_obj?.role

  const currentUser = useUserData()
  const isTeamHidden = useIsTeamVisibilityHidden()

  const isAuthorProfileHidden = isTeamUserProfileHidden({
    currentUser,
    isTeamHidden,
    user: message.owner_obj,
  })

  const profileToLink = isAuthorProfileHidden
    ? null
    : getUserProfileRoute({
        id: message.owner,
        isProviderProfileNGAvailable,
        isUserProfileNGAvailable,
        role,
      })

  const headerText = getChatroomMessageDisplayHeaderText({
    companyPostName: message.owner_obj?.company_post_name,
    fullName,
    isOwnMessage,
    role,
    t,
  })

  const { handleToggleLike } = useChatroomMessageLike({
    message,
    outboundUuid,
    onUpdateMessage,
  })

  const rootClassName = classNames(styles.chatroomMessage, {
    [styles.chatroomMessageOwn]: isOwnMessage,
  })

  const columnClassName = classNames(styles.column, {
    [styles.columnOwn]: isOwnMessage,
  })

  return (
    <div className={rootClassName}>
      <ChatroomMessageHeader
        avatar={message.owner_obj?.avatar}
        color={message.owner_obj?.avatar_color}
        fullName={fullName}
        headerText={headerText}
        profileToLink={profileToLink}
      />
      <div className={columnClassName}>
        <ChatroomMessageBody
          filePreviewProps={filePreviewProps}
          files={files}
          isLiked={isLiked}
          isOwnMessage={isOwnMessage}
          isPrivate={isPrivate}
          likeCount={likeCount}
          message={message}
          outboundUuid={outboundUuid}
          timeLabel={timeLabel}
          onEditMessage={onEditMessage}
          onLike={handleToggleLike}
          onRefetchVisibleMessages={onRefetchVisibleMessages}
          onUpdateMessage={onUpdateMessage}
        />
      </div>
    </div>
  )
}

export default ChatroomMessage
